import { useEffect, useState, useCallback } from 'react';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../hooks/useAppSelector';
import { Card, CardHeader, CardContent } from '@mui/material';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useIsInternalUser } from '../../../hooks/useIsInternalUser';
import type { Vehicle } from '../../../data/models/vehicle.model';
import * as vehicleSlice from '../../../data/vehicle.slice';
import * as referenceSlice from '../../../data/reference.slice';
import TextInput from '../../form/TextInput';
import SelectMenu from '../../form/SelectMenu';
import validationUtils from '../../../services/validationUtils';
import vehicleUtils from '../../../services/vehicleUtils';
import DateInput from '../../form/DateInput';
import ElementDialog, { formClasses } from '../ElementDialog';
import ActiveClear from '../../form/ActiveClear';
import documentUtils from '../../../services/documentUtils';
import type { BaseProvider } from '../../../data/models/provider.model';
import CheckboxInput from '../../form/CheckboxInput';
import { DateTime } from 'luxon';

interface VehicleDialogProps {
  title: string;
  open: boolean;
  provider: BaseProvider;
  vehicle?: Vehicle | null;
  close: (saved: boolean) => void;
}

export default function VehicleDialog({
  title,
  open,
  provider,
  vehicle,
  close,
}: VehicleDialogProps) {
  const { t } = useTranslation('common');
  const requiredFieldMessage = t('common.requiredField');
  const dispatch = useAppDispatch();
  const isInternalUser = useIsInternalUser();
  const isProviderInternal = provider?.isInternal;

  const [minDate, setMinDate] = useState<DateTime>();
  const [maxDate, setMaxDate] = useState<DateTime>();
  const [hasDocuments, setHasDocuments] = useState(false);
  const [providerIsCTT, setProviderIsCTT] = useState(false);
  const [disabledMessage, setDisabledMessage] = useState('');

  const stateCodes = useAppSelector(referenceSlice.selectStateCodes);
  const vehicleTypes = useAppSelector(referenceSlice.selectVehicleTypes);
  const colors = useAppSelector(referenceSlice.selectVehicleColors);
  const attributeTypes = useAppSelector(
    referenceSlice.selectVehicleAttributeTypes,
  );
  const documentTypes = useAppSelector(
    referenceSlice.selectVehicleDocumentTypes,
  );
  const documents = useAppSelector(vehicleSlice.selectVehicleDocuments);

  const saving = useAppSelector(vehicleSlice.selectSavingVehicle);
  const saved = useAppSelector(vehicleSlice.selectSavedVehicle);
  const savingError = useAppSelector(vehicleSlice.selectSavingError);

  const { control, formState, handleSubmit, reset, watch, setValue } = useForm({
    defaultValues: vehicleUtils.getDefaults(vehicle, attributeTypes),
  });

  const isCTTChecked = watch('IsClearToTransport');
  const isInternalChecked = watch('IsInternal');

  // This is here so after a save, the updated vehicle is used to populate the form values
  useEffect(() => {
    reset(vehicleUtils.getDefaults(vehicle, attributeTypes));
  }, [open, vehicle, attributeTypes, reset]);

  useEffect(() => {
    if (documentTypes && documents) {
      setHasDocuments(
        documentUtils.hasAllRequiredDocuments(documentTypes, documents),
      );
    }
  }, [documentTypes, documents]);

  useEffect(() => {
    const ctt = !!provider?.isClearToTransport;
    setProviderIsCTT(ctt);
  }, [provider]);

  useEffect(() => {
    let msg = '';
    if (!isCTTChecked) {
      // For now there are no required driver documents so this is commented out.
      // if (!driver || !hasDocuments) {
      //   msg += t('common.missingDocuments') + ' ';
      // }
      if (!providerIsCTT) {
        msg += t('common.providerNotCTT');
      }
    }
    setDisabledMessage(msg);
  }, [isCTTChecked, hasDocuments, providerIsCTT, vehicle, t]);

  useEffect(() => {
    if (isInternalUser && isProviderInternal) {
      setValue('IsInternal', true);
    }
  }, [isInternalUser, isProviderInternal, setValue]);

  useEffect(() => {
    if (vehicle?.vehicleID) {
      dispatch(vehicleSlice.fetchVehicleDocumentsAsync(vehicle.vehicleID));
    } else {
      dispatch(vehicleSlice.setVehicleDocuments([]));
    }
  }, [vehicle, dispatch]);

  useEffect(() => {
    setMinDate(DateTime.now().startOf('day'));
    setMaxDate(DateTime.now().plus({ years: 15 }).endOf('year'));
  }, []);

  const saveVehicle = useCallback(
    (data: any) => {
      const id = vehicle ? vehicle.vehicleID : undefined;
      const v = vehicleUtils.getVehicleFromFormData(
        provider.transportationProviderID!,
        id,
        data,
        attributeTypes,
      );
      const fn = id
        ? vehicleSlice.updateVehicleAsync(v)
        : vehicleSlice.createVehicleAsync(v);
      dispatch(fn);
    },
    [attributeTypes, dispatch, provider, vehicle],
  );

  return (
    <ElementDialog
      title={title}
      faqContext="vehicles_details_form"
      open={open}
      saving={saving}
      saved={saved}
      savingError={savingError}
      save={handleSubmit(saveVehicle)}
      reset={reset}
      close={close}
      formState={formState}>
      <Card className={formClasses.card}>
        <CardHeader title={t('vehicle.info')} />
        <CardContent className={formClasses.cardContent}>
          <TextInput
            label={t('vehicle.license')}
            name="LicensePlate"
            required={true}
            control={control}
            errors={formState.errors}
            rules={{ required: requiredFieldMessage }}
            inputProps={{ maxLength: 10 }}
          />
          <SelectMenu
            label={t('vehicle.state')}
            name="LicenseStateCode"
            values={stateCodes}
            valueAttr="stateCode"
            labelAttr="stateName"
            required={true}
            control={control}
            errors={formState.errors}
            rules={{ required: requiredFieldMessage }}
          />
          <DateInput
            label={t('vehicle.regExpires')}
            name="RegistrationExpiresOn"
            control={control}
            required={true}
            errors={formState.errors}
            minDate={minDate}
            maxDate={maxDate}
          />
          <TextInput
            label={t('vehicle.vin')}
            name="VehicleVIN"
            required={true}
            control={control}
            errors={formState.errors}
            rules={{
              required: requiredFieldMessage,
              validate: validationUtils.vinValidation(t),
            }}
            inputProps={{ maxLength: 17 }}
          />
          <SelectMenu
            label={t('vehicle.type')}
            name="VehicleTypeID"
            values={vehicleTypes}
            valueAttr="vehicleTypeID"
            labelAttr="vehicleTypeName"
            required={true}
            control={control}
            errors={formState.errors}
            rules={{ required: requiredFieldMessage }}
          />
          <TextInput
            label={t('vehicle.make')}
            name="VehicleMake"
            required={true}
            control={control}
            errors={formState.errors}
            rules={{ required: requiredFieldMessage }}
            inputProps={{ maxLength: 20 }}
          />
          <TextInput
            label={t('vehicle.model')}
            name="VehicleModel"
            required={true}
            control={control}
            errors={formState.errors}
            rules={{ required: requiredFieldMessage }}
            inputProps={{ maxLength: 20 }}
          />
          <TextInput
            label={t('vehicle.year')}
            name="VehicleYear"
            required={true}
            control={control}
            errors={formState.errors}
            rules={{
              required: requiredFieldMessage,
              validate: validationUtils.rangeValidation(2000, 2099, t),
            }}
            type="number"
            inputProps={{ maxLength: 4, step: 1, min: 2000, max: 2099 }}
          />
          <SelectMenu
            label={t('vehicle.color')}
            name="VehicleColorID"
            values={colors}
            valueAttr="vehicleColorName"
            labelAttr="vehicleColorName"
            required={true}
            control={control}
            errors={formState.errors}
            rules={{ required: requiredFieldMessage }}
          />
        </CardContent>
      </Card>

      <Card className={formClasses.card}>
        <CardHeader title="Attributes" />
        <CardContent className={formClasses.cardContent}>
          <ActiveClear
            control={control}
            hideClearToTransport={false}
            hideReadyClearToTransport={true}
            disabledMessage={disabledMessage ? disabledMessage : undefined}
          />
          {isInternalUser && (
            <CheckboxInput
              label={t('vehicle.isInternal')}
              name="IsInternal"
              required={false}
              control={control}
              disabled={isProviderInternal && isInternalChecked}
            />
          )}
          {attributeTypes.map((attr) => {
            return (
              <TextInput
                label={attr.vehicleAttributeTypeName}
                name={`attr-${attr.vehicleAttributeTypeID}`}
                key={attr.vehicleAttributeTypeID}
                control={control}
                errors={formState.errors}
                type="number"
                rules={{
                  validate: validationUtils.rangeValidation(0, 255, t),
                }}
                inputProps={{
                  maxLength: 1,
                  step: 1,
                  min: 0,
                  max: 255,
                }}
              />
            );
          })}
        </CardContent>
      </Card>
    </ElementDialog>
  );
}
